import { useEffect, useState } from "preact/hooks";
import { PixelIcon } from "./PixelArt";
import { api } from "../api";
import { useRushPickUI } from "../features";
import type { FlashState } from "../../shared/types";
import {
  NO_CHOICES,
  PICKED_BODY,
  PICK_BODY,
  PICK_TITLE,
  pickConfirm,
  pickedRushBody,
  pickedRushTitle,
  pickedTitle,
  pickerLine,
  type FlashPickState,
} from "../../shared/flash.ts";
import { LATE_RUSH_TITLE, isLateRushActive, lateRushBody } from "../../shared/late-rush.ts";

/** Minutes left in `endsAt`'s window, never negative. */
function minutesLeft(endsAt: string, now: number): number {
  return Math.max(0, Math.ceil((new Date(endsAt).getTime() - now) / 60_000));
}

/** Where the twinkles sit, all on the frame's band and never over the words (`.flash-sparkle-*`). */
const SPARKLES = ["tl", "top", "right", "br", "bottom"] as const;

/**
 * The gold rush pick as this peon sees it (GET /api/flash/pick), read while `rushPickUI` is on:
 * on mount, for a new name, and when a rush starts or ends. Null otherwise.
 */
function usePick(peonName: string, rushId: string | null): [FlashPickState | null, (p: FlashPickState) => void] {
  const on = useRushPickUI();
  const [pick, setPick] = useState<FlashPickState | null>(null);
  useEffect(() => {
    if (!on || !peonName) {
      setPick(null);
      return;
    }
    let live = true;
    api.flash
      .pick(peonName)
      .then((p) => live && setPick(p))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [on, peonName, rushId]);
  return [on ? pick : null, setPick];
}

/**
 * The open board's gold rush card, between PinnedNotes and the task list.
 * Hidden entirely when there is no active rush and nothing to pick; the countdown (useMinuteTick,
 * passed in as `now`) keeps it live without a network request. It sits in the gold frame with a
 * few pixel glints (public/ui/sparkle-sheet.png, scripts/make-sparkle-sprites.mjs) twinkling on the
 * frame's band; with reduced motion two of them hold still and the rest are not drawn.
 *
 * With `rushPickUI` on (docs/bounties.md "Gold rush pick") the picker alone sees it the evening
 * before, offering the open tasks as a compact list (titles only, 44px rows, one tap and the
 * browser's confirm); a picked rush reads "Gold rush: Run Dishwasher" with "Hue's pick" under it.
 *
 * With `lateRushUI` on (docs/bounties.md "Late chore rush") the viewer's own late chore rush window
 * (flash.late_rush, present only while that flag is on: shared/flash.ts flashForFlags) reads "Gold
 * rush: late chores" / "Double gold on every late chore for N min." in the same frame, when no gold
 * rush is on (a gold rush, the rarer, keeps the banner; the late chores still pay double meanwhile).
 */
export function FlashBanner({ flash, now, peonName = "" }: { flash: FlashState | null; now: number; peonName?: string }) {
  const rush = flash?.active ?? null;
  const [pick, setPick] = usePick(peonName, rush?.id ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const late = !rush && isLateRushActive(flash?.late_rush, now) ? flash!.late_rush! : null;
  const picking = !rush && !late && !!pick?.mine && !!pick.closes_at && now < Date.parse(pick.closes_at);
  if (!rush && !late && !picking) return null;

  async function choose(id: string, title: string) {
    if (busy || !confirm(pickConfirm(title))) return;
    setBusy(true);
    setError(null);
    try {
      setPick(await api.flash.choose(peonName, id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "That did not work. Try again.");
    } finally {
      setBusy(false);
    }
  }

  let head: string;
  let byline: string | null = null;
  let body: string;
  if (rush) {
    const mins = minutesLeft(rush.ends_at, now);
    if (rush.task_title) {
      head = pickedRushTitle(rush.task_title);
      byline = rush.picker ? pickerLine(rush.picker) : null;
      body = flash!.claimed ? `You cashed in this gold rush. Nice. ${mins} min left.` : pickedRushBody(mins, rush.multiplier);
    } else {
      head = "Gold rush!";
      body = flash!.claimed ? `You cashed in this gold rush. Nice. ${mins} min left.` : `Your next task in the next ${mins} min pays ${rush.multiplier}x gold.`;
    }
  } else if (late) {
    head = LATE_RUSH_TITLE;
    body = lateRushBody(Math.max(1, minutesLeft(late.ends_at, now)));
  } else if (pick!.pick) {
    head = pickedTitle(pick!.pick.title);
    body = PICKED_BODY;
  } else {
    head = PICK_TITLE;
    body = pick!.choices.length ? PICK_BODY : NO_CHOICES;
  }
  const choices = !rush && !late && !pick!.pick ? pick!.choices : [];

  return (
    <div class="flash-banner" role="status">
      {SPARKLES.map((at) => (
        <span key={at} class={`flash-sparkle flash-sparkle-${at}`} aria-hidden="true" />
      ))}
      <div class="flash-banner-head">
        <PixelIcon name="flame" />
        <span class="flash-banner-title">{head}</span>
      </div>
      {byline && <p class="flash-banner-byline">{byline}</p>}
      <p class="flash-banner-body">{body}</p>
      {choices.length > 0 && (
        <ul class="flash-pick-list" aria-label="Tasks to pick">
          {choices.map((c) => (
            <li key={c.id}>
              <button type="button" class="flash-pick-row" disabled={busy} onClick={() => void choose(c.id, c.title)}>
                {c.title}
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p class="flash-banner-error muted small">{error}</p>}
    </div>
  );
}
